import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { Action } from './action'

export interface Filter<out TAction extends Action.Constraint> {
  readonly check: TypeChecker<TAction>
  readonly applyFilter: () => rxjs.OperatorFunction<unknown, TAction>
}
export namespace Filter {
  export interface WithObservable<out TAction extends Action.Constraint>
    extends Filter<TAction> {
    readonly observe: () => rxjs.Observable<TAction>
  }
  export namespace WithObservable {
    export function on<TAction extends Action.Constraint>(
      filter: Filter<TAction>,
      source$: rxjs.Observable<unknown>,
    ): WithObservable<TAction> {
      return {
        ...filter,
        observe: () => source$.pipe(filter.applyFilter()),
      }
    }
  }

  export function from<TAction extends Action.Constraint>(
    typeChecker: TypeChecker<TAction>,
  ): Filter<TAction> {
    return {
      applyFilter: () => rxjs.filter(typeChecker),
      check: typeChecker,
    }
  }

  export function where<TAction extends Action.Constraint>(
    check: (action: Action<any>) => action is TAction,
  ): Filter<TAction>
  export function where(check: (action: Action) => boolean): Filter<Action>
  export function where(check: (action: Action) => boolean): Filter<Action> {
    return from(
      TypeChecker.Factory.intersection(
        Action.isAction,
        TypeChecker.isTypeChecker(check)
          ? check
          : TypeChecker.Factory.custom(
              // NOTE: needs to be cast as a type checker even though we are not
              // actually narrowing the type
              check satisfies (action: Action) => boolean as (
                action: Action,
              ) => action is Action,
              {
                typeDescription: 'action.Filter.where',
              },
            ),
      ),
    )
  }

  /**
   * Creates a single {@link Action.Filter} that matches an {@link Action} if
   * any of the given
   * {@link Action.Filter} matches that {@link Action}
   *
   * @see {@link OneOf}
   */
  export function oneOf<TFilters extends readonly Filter<any>[]>(
    ...filters: TFilters
  ): OneOf<TFilters> {
    return from(
      TypeChecker.Factory.union(...filters.map(filter => filter.check)),
    ) satisfies OneOf<TFilters> as OneOf<TFilters>
  }
  /**
   * Return type of {@link Action.Filter.oneOf}
   */
  export type OneOf<TFilters extends readonly Filter<any>[]> = Filter<
    {
      [K in keyof TFilters]: TFilters[K] extends Filter<infer TAction>
        ? TAction
        : never
    }[number]
  >
}
